import {
  Button,
  type ButtonProps,
  type SxProps,
  type Theme,
} from '@mui/material'
import { type ReactNode, useMemo } from 'react'
import DevicesLink from '../DevicesLink'

type CmsButtonProps = Omit<ButtonProps, 'color' | 'href' | 'children'> & {
  href: string
  backgroundColor?: string
  backgroundColour?: string
  color?: string
  textColour?: string
  target?: string
  rel?: string
  children: ReactNode
}

export function CmsButton({
  href,
  backgroundColor,
  backgroundColour,
  color,
  textColour,
  fullWidth,
  target,
  rel,
  children,
  sx,
  ...props
}: CmsButtonProps) {
  const background = backgroundColor ?? backgroundColour
  const foreground = color ?? textColour

  const { variant, buttonSx } = useMemo(() => {
    const sxArray = !sx ? [] : Array.isArray(sx) ? sx : [sx]

    const layoutSx: SxProps<Theme> = {
      minWidth: fullWidth ? '100%' : '9rem',
      width: fullWidth ? '100%' : 'auto',
      whiteSpace: 'nowrap',
      fontWeight: 700,
    }

    let colourSx: SxProps<Theme> = {}

    if (background === 'transparent' && foreground) {
      colourSx = {
        color: foreground,
        borderColor: foreground,
        backgroundColor: 'transparent',
        '&:hover': {
          color: foreground,
          borderColor: foreground,
          backgroundColor: 'transparent',
        },
      }
    } else if (background) {
      colourSx = {
        color: foreground,
        backgroundColor: background,
        '&:hover': { backgroundColor: background },
      }
    } else if (foreground) {
      colourSx = { color: foreground }
    }

    return {
      variant:
        background === 'transparent'
          ? ('outlined' as const)
          : ('contained' as const),
      buttonSx: [layoutSx, colourSx, ...sxArray] as SxProps<Theme>,
    }
  }, [background, foreground, fullWidth, sx])

  return (
    <Button
      component={DevicesLink}
      href={href}
      target={target}
      rel={rel}
      variant={variant}
      disableElevation
      sx={buttonSx}
      {...props}
    >
      {children}
    </Button>
  )
}
